import { z } from "zod";
import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import {
  ApiResult,
  DropdownOption,
  ProductionMutationResponse,
  ProductionRecipeItem,
  ReceivingItem,
  SaveScannedItemPayload,
  UpdateScannedItemPayload,
} from "../employeeProductionTypes";
import {
  dropdownOptionSchema,
  productionMutationSchema,
  productionScannedItemsSchema,
  receivingItemResponseSchema,
} from "./employeeProductionSchemas";

type LegacyCallResult = {
  success: boolean;
  data?: unknown;
  errorStatus?: number;
};

function parseResult<T>(
  result: LegacyCallResult,
  schema: z.ZodType<T>,
): ApiResult<T> {
  if (!result?.success) {
    const data = result?.data as
      | { message?: string; subtitle?: string }
      | undefined;
    return {
      success: false,
      status: result?.errorStatus,
      message:
        data?.message ?? data?.subtitle ?? "We could not complete the action.",
    };
  }

  const parsed = schema.safeParse(result.data);
  if (!parsed.success) {
    return {
      success: false,
      message: "The server returned an unexpected production response.",
    };
  }
  return { success: true, data: parsed.data };
}

export async function fetchProductionLots(): Promise<
  ApiResult<DropdownOption[]>
> {
  const result = (await makeGetApiCall("lotsForDropdown")) as LegacyCallResult;
  return parseResult(result, z.array(dropdownOptionSchema));
}

export async function fetchProductionRecipe(
  productionId: number,
): Promise<ApiResult<ProductionRecipeItem[]>> {
  const result = (await makeGetApiCall(
    `getProductionScannedItems/${productionId}`,
  )) as LegacyCallResult;
  const parsed = parseResult(result, productionScannedItemsSchema);
  return parsed.success
    ? { success: true, data: parsed.data.recipe_items }
    : parsed;
}

export async function fetchReceivingItem(
  barcode: string,
): Promise<ApiResult<ReceivingItem>> {
  const result = (await makeGetApiCall(
    `receivingItem/${encodeURIComponent(barcode)}`,
  )) as LegacyCallResult;
  return parseResult(result, receivingItemResponseSchema);
}

export async function saveProductionItem(
  payload: SaveScannedItemPayload,
): Promise<ApiResult<ProductionMutationResponse>> {
  const result = (await makePostApiCall(
    "saveScannedItemToProduction",
    payload,
  )) as LegacyCallResult;
  return parseResult(result, productionMutationSchema);
}

export async function updateProductionItem(
  payload: UpdateScannedItemPayload,
): Promise<ApiResult<ProductionMutationResponse>> {
  const result = (await makePostApiCall(
    "updateScannedItemForProduction",
    payload,
  )) as LegacyCallResult;
  return parseResult(result, productionMutationSchema);
}

export async function deleteProductionItem(
  productionId: number,
  barcode: string,
): Promise<ApiResult<ProductionMutationResponse>> {
  const result = (await makePostApiCall("deleteScannedItemForProduction", {
    production_id: productionId,
    barcode,
  })) as LegacyCallResult;
  return parseResult(result, productionMutationSchema);
}
